/**
 * Merlin Extensions - Browser API Wrapper
 * 
 * Cross-browser compatible wrapper for Chrome/Firefox extension APIs.
 * Use this instead of directly accessing chrome.* or browser.* APIs.
 */

// Detect browser and use appropriate API
const browserAPI = (() => {
    // Firefox uses browser.* with Promises
    if (typeof browser !== 'undefined') {
        return browser;
    }
    // Chrome uses chrome.* with callbacks (but supports Promises in MV3)
    if (typeof chrome !== 'undefined') {
        return chrome;
    }
    // Fallback for non-extension environments (e.g., Dash)
    return null;
})();

/**
 * Check if we're running as a browser extension
 */
const isExtension = () => browserAPI !== null && typeof browserAPI.runtime !== 'undefined';

/**
 * Get a resource URL that works in both extension and non-extension contexts
 * @param {string} path - Relative path to resource
 * @returns {string} Full URL to resource
 */
const getResourceURL = (path) => {
    if (isExtension()) {
        return browserAPI.runtime.getURL(path);
    }
    // In non-extension context, use relative path
    return path;
};

/**
 * Storage wrapper with Promise-based API
 */
const storage = {
    async get(keys) {
        if (!isExtension()) {
            // Fallback to localStorage
            const result = {};
            const keyArray = Array.isArray(keys) ? keys : [keys];
            keyArray.forEach(key => {
                const value = localStorage.getItem(`merlin_${key}`);
                if (value !== null) {
                    try {
                        result[key] = JSON.parse(value);
                    } catch {
                        result[key] = value;
                    }
                }
            });
            return result;
        }
        return browserAPI.storage.local.get(keys);
    },
    
    async set(items) {
        if (!isExtension()) {
            // Fallback to localStorage
            Object.entries(items).forEach(([key, value]) => {
                localStorage.setItem(`merlin_${key}`, JSON.stringify(value));
            });
            return;
        }
        return browserAPI.storage.local.set(items);
    },
    
    async remove(keys) {
        if (!isExtension()) {
            const keyArray = Array.isArray(keys) ? keys : [keys];
            keyArray.forEach(key => localStorage.removeItem(`merlin_${key}`));
            return;
        }
        return browserAPI.storage.local.remove(keys);
    }
};

/**
 * Message passing wrapper
 */
const messaging = {
    /**
     * Send a message to the background script
     * @param {object} message - Message to send
     * @returns {Promise} Response from background
     */
    async sendMessage(message) {
        if (!isExtension()) {
            console.warn('messaging.sendMessage called outside extension context');
            return null;
        }
        return browserAPI.runtime.sendMessage(message);
    },
    
    /**
     * Add a message listener
     * @param {function} callback - (message, sender, sendResponse) => void
     */
    onMessage(callback) {
        if (!isExtension()) {
            // Use window.postMessage as fallback
            window.addEventListener('message', (event) => {
                if (event.data?.type?.startsWith('MERLIN_')) {
                    callback(event.data, { origin: event.origin }, (response) => {
                        window.postMessage({ type: 'MERLIN_RESPONSE', response }, '*');
                    });
                }
            });
            return;
        }
        browserAPI.runtime.onMessage.addListener(callback);
    },
    
    /**
     * Post a message to the page (for content scripts)
     * @param {object} message - Message to post
     */
    postToPage(message) {
        window.postMessage({ ...message, type: `MERLIN_${message.type || 'MESSAGE'}` }, '*');
    }
};

// Export for both ES modules and browser globals
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { browserAPI, isExtension, getResourceURL, storage, messaging };
}

if (typeof window !== 'undefined') {
    window.MerlinBrowserAPI = { browserAPI, isExtension, getResourceURL, storage, messaging };
}

export { browserAPI, isExtension, getResourceURL, storage, messaging };
